import type { ReactElement } from 'react';
import { createElement } from 'react';
import { Icon } from '@material-ui/core';
import { isBefore, isAfter, startOfDay, endOfDay } from 'date-fns';

import { FileObject, MimeTypes } from '@klp/file/classes';
import { fromUTC, stripHtml } from '@klp/helpers';
import { generateRouteParams } from '@klp/routing';

import { MessageBoardRoutes } from '../config';

import { ByggKunde } from './ByggKunde';
import type { IMessage, IMessageFix } from './IMessage';
import type { IMessageInput } from './IMessageInput';
import { MessageCategory } from './MessageCategory';
import { MessageStatus } from './MessageStatus';

const statusIcons = {
    [MessageStatus.Draft]: 'description',
    [MessageStatus.Published]: 'publish',
    [MessageStatus.Expired]: 'timer_off',
    [MessageStatus.Deleted]: 'delete',
    [MessageStatus.WillPublish]: 'alarm_on',
};

export class Message implements IMessage {
    private _id?: number;
    private _category: MessageCategory;
    private _title: string;
    private _content: string;
    private _status: MessageStatus;
    private _createdAt?: Date;
    private _createdBy?: number;
    private _createdByName?: string;
    private _updatedAt?: Date;
    private _updatedBy?: number;
    private _updatedByName?: string;
    private _publishAt?: Date;
    private _expiresAt?: Date;
    private _notificationSentAt?: Date;
    private _notifyByEMail: boolean;
    private _sendImmediately?: boolean;
    private _attachments: Array<FileObject>;
    private _isPinned: boolean;

    private _mottakereList: Array<ByggKunde>;
    private _messageFiles: Array<FileObject>;

    public static parseObject(obj: IMessageInput): Message {
        try {
            return new Message(obj);
        } catch (e) {
            throw new Error(
                `Couldn't parse object: ${JSON.stringify(obj)} ${e}`,
            );
        }
    }

    public static empty(): Message {
        return Message.parseObject({
            category: MessageCategory.Info,
            title: '',
            content: '',
            status: MessageStatus.Draft,
            isPinned: false,
        });
    }

    public static icon(status: MessageStatus): ReactElement {
        return createElement(Icon, { children: statusIcons[status] });
    }

    constructor(obj: IMessageInput & IMessageFix) {
        const {
            id,
            category,
            title,
            content,
            status,
            createdAt,
            createdBy,
            createdByName,
            updatedAt,
            updatedBy,
            updatedByName,
            publishAt,
            expiresAt,
            publishedAt,
            expiredAt,
            mottakereList,
            attachments,
            messageImages,
            messageAttachments,
            isPinned,
        } = obj;

        this._id = id;
        this._category =
            MessageCategory[category as keyof typeof MessageCategory];
        this._title = title;
        this._content = content;
        this._status = MessageStatus[status as keyof typeof MessageStatus];

        this._createdAt = createdAt ? fromUTC(createdAt) : undefined;
        this._createdBy = createdBy;
        this._createdByName = createdByName;

        this._updatedAt = updatedAt ? fromUTC(updatedAt) : undefined;
        this._updatedBy = updatedBy;
        this._updatedByName = updatedByName;

        // this._publishAt = publishAt ? new Date( publishAt ) : undefined;
        // Temporarily using "publishedAt"
        this._publishAt = publishAt
            ? fromUTC(publishAt)
            : publishedAt
              ? fromUTC(publishedAt)
              : undefined;

        // this._expiresAt = expiresAt ? new Date( expiresAt ) : undefined;
        // Temporarily using "expiredAt"
        this._expiresAt = expiresAt
            ? fromUTC(expiresAt)
            : expiredAt
              ? fromUTC(expiredAt)
              : undefined;

        this._mottakereList = mottakereList
            ? mottakereList.map((item) => new ByggKunde(item))
            : [];

        this._notificationSentAt = obj.notificationSentAt
            ? fromUTC(obj.notificationSentAt)
            : undefined;
        this._notifyByEMail = !!obj.notifyByEMail;
        this._sendImmediately = !!obj.sendImmediately;
        this._messageFiles = [
            ...(messageImages || []),
            ...(messageAttachments || []),
        ];
        this._attachments =
            attachments?.map((attachment) => new FileObject(attachment)) ?? [];
        this._isPinned = isPinned;
    }

    public get id() {
        return this._id;
    }

    public get category() {
        return this._category;
    }

    public get title() {
        return this._title;
    }

    public get content() {
        return this._content;
    }

    public get status() {
        return this._status;
    }

    public get createdAt() {
        return this._createdAt;
    }

    public get createdBy() {
        return this._createdBy;
    }

    public get createdByName() {
        return this._createdByName;
    }

    public get updatedAt() {
        return this._updatedAt;
    }

    public get notificationSentAt() {
        return this._notificationSentAt;
    }

    public get updatedBy() {
        return this._updatedBy;
    }

    public get updatedByName() {
        return this._updatedByName;
    }

    public get publishAt() {
        return this._publishAt;
    }

    public get expiresAt() {
        return this._expiresAt;
    }

    public get dataModel() {
        const {
            _id,
            _title,
            _category,
            _content,
            _status,
            _publishAt,
            _expiresAt,
            _mottakereList,
            _notifyByEMail,
            _sendImmediately,
            _attachments,
            _isPinned,
        } = this;
        const model = {
            id: _id,
            title: _title,
            category: _category,
            content: _content,
            publishAt: _publishAt,
            expiresAt: _expiresAt,
            status: _status,

            publishedAt: _publishAt ? startOfDay(_publishAt) : undefined,
            expiredAt: _expiresAt ? endOfDay(_expiresAt) : undefined,

            mottakereList: _mottakereList.map((byggKunde) => ({
                kundeNummer: byggKunde.kundeNummer,
                kundeNavn: byggKunde.kundeNavn,
                byggNummer: byggKunde.byggNummer,
                byggNavn: byggKunde.byggNavn,
            })),
            notifyByEMail: _notifyByEMail,
            sendImmediately: _sendImmediately,
            attachments: _attachments,
            isPinned: _isPinned,
        };
        return model;
    }

    public get mottakereList() {
        return this._mottakereList;
    }

    public get notifyByEMail() {
        return this._notifyByEMail;
    }

    public get sendImmediately() {
        return this._sendImmediately;
    }

    public get messageFiles() {
        return this._messageFiles;
    }

    public get attachments() {
        return this._attachments;
    }

    public get attachmentsImages() {
        return this._attachments.filter(({ type }) =>
            MimeTypes.image.includes(type || ''),
        );
    }
    public get attachmentsDocuments() {
        return this._attachments.filter(({ type }) =>
            MimeTypes.document.includes(type || ''),
        );
    }

    public get categoryIcon() {
        const { category } = this;
        const { Warning, Alert } = MessageCategory;

        switch (category) {
            case Warning:
                return createElement(Icon, {
                    children: 'warning',
                    className: 'CategoryIcon CategoryIcon--Warning',
                });
            case Alert:
                return createElement(Icon, {
                    children: 'error',
                    className: 'CategoryIcon CategoryIcon--Alert',
                });
            default:
                return createElement(Icon, {
                    children: 'done',
                    className: 'CategoryIcon CategoryIcon--Info',
                });
        }
    }

    public get messageExpired() {
        return this.expiresAt ? isBefore(this.expiresAt, new Date()) : false;
    }

    public get messageWillPublish() {
        return this.publishAt ? isAfter(this.publishAt, new Date()) : false;
    }

    public get adminStatus() {
        const { status, messageExpired, messageWillPublish } = this;
        const { Expired, Published, WillPublish } = MessageStatus;

        if (status === Published && messageExpired) {
            return Expired;
        } else if (status === Published && messageWillPublish) {
            return WillPublish;
        }

        return status;
    }

    public get adminIcon() {
        const { status, messageExpired, messageWillPublish } = this;

        const { Expired, Published, WillPublish } = MessageStatus;

        if (status === Published && messageExpired) {
            return createElement(Icon, {
                children: statusIcons[Expired],
                className: 'MessageAdminIcon',
            });
        }
        if (status === Published && messageWillPublish) {
            return createElement(Icon, {
                children: statusIcons[WillPublish],
                className: 'MessageAdminIcon',
            });
        }

        return createElement(Icon, {
            children: statusIcons[status],
            className: 'MessageAdminIcon',
        });
    }

    public get ingress() {
        const { content } = this;
        return content.length > 175
            ? content.substr(0, content.lastIndexOf(' ', 175)) + '…'
            : content;
    }

    public get ingressStripped() {
        return stripHtml(this.ingress);
    }

    public get thumbnailUrl() {
        const { id, attachmentsImages } = this;
        if (attachmentsImages.length === 0) {
            return '';
        }
        return MessageBoardRoutes.thumbnail(id, attachmentsImages[0].id).concat(
            generateRouteParams({
                maxwidth: 450,
                maxheight: 300,
                trimToFit: false,
            }),
        );
    }

    public get isPinned() {
        return this._isPinned;
    }

    public get vedleggList() {
        return this._attachments;
    }

    public get vedleggListLength(): boolean {
        return Array.isArray(this.vedleggList) && this.vedleggList.length > 0;
    }
}
